import {
  deriveActiveHierarchyMode,
  HierarchyMode,
} from "../../AsnGap/components/LocationSummaryV2Hierarchy";
import type { MockGrain } from "../../AsnGap/components/LocationSummaryV2MockData";

/**
 * Region -> Market -> Location rollup rows for the On-Time Dispatch Location
 * Summary table. Mirrors ASN Gap's Location Summary (LocationSummaryV2Hierarchy /
 * buildRegionHierarchyRows / buildMarketHierarchyRows): the mode is derived with
 * the shared `deriveActiveHierarchyMode`, and rollup rows are expandable.
 */

export type { HierarchyMode };

export type HierarchyGroupType = "region" | "market" | "location";

export interface HierarchyRowMeta {
  groupType?: HierarchyGroupType;
  parentRegionExpandKey?: string;
  parentMarketExpandKey?: string;
}

interface HierarchyMetricFields {
  planned: number;
  completed: number;
  incomplete: number;
  onTime: number;
  daeN: number;
  daeD: number;
  completedPct: number;
  onTimePct: number;
  daePct: number;
}

type HierarchyBaseRow = HierarchyMetricFields &
  HierarchyRowMeta & {
    id?: string;
    locationId?: string;
    dimensions: Record<string, string>;
  };

type RowComparator<T> = (a: T, b: T) => number;

/** Dimension keys of the visible columns, in on-screen order -> hierarchy mode. */
export const deriveLocationSummaryHierarchyMode = (
  dimensionKeys: string[]
): HierarchyMode => deriveActiveHierarchyMode(dimensionKeys as MockGrain[]);

const sumMetrics = (rows: HierarchyMetricFields[]): HierarchyMetricFields => {
  const total = rows.reduce(
    (acc, row) => {
      acc.planned += row.planned;
      acc.completed += row.completed;
      acc.incomplete += row.incomplete;
      acc.onTime += row.onTime;
      acc.daeN += row.daeN;
      acc.daeD += row.daeD;
      return acc;
    },
    { planned: 0, completed: 0, incomplete: 0, onTime: 0, daeN: 0, daeD: 0 }
  );
  return {
    ...total,
    // Percentages are always recomputed from the summed counts (never averaged).
    completedPct: total.planned ? (total.completed / total.planned) * 100 : 0,
    onTimePct: total.planned ? (total.onTime / total.planned) * 100 : 0,
    daePct: total.daeD ? (total.daeN / total.daeD) * 100 : 0,
  };
};

const groupBy = <T>(rows: T[], keyOf: (row: T) => string): Map<string, T[]> => {
  const groups = new Map<string, T[]>();
  rows.forEach((row) => {
    const key = keyOf(row);
    const bucket = groups.get(key);
    if (bucket) bucket.push(row);
    else groups.set(key, [row]);
  });
  return groups;
};

/**
 * The API always returns rows at location x year x period x week level. Rows that
 * are identical on the visible dimension columns are summed into one row (counts
 * added, percentages recomputed) so every displayed row is unique for its columns.
 */
export const aggregateLocationSummaryRowsByDimensions = <T extends HierarchyBaseRow>(
  rows: T[],
  dimensionKeys: string[]
): T[] => {
  if (dimensionKeys.length === 0) return rows;
  const groups = groupBy(rows, (row) =>
    dimensionKeys.map((key) => row.dimensions[key] ?? "").join("\u0001")
  );
  return Array.from(groups.entries()).map(([key, group]) =>
    group.length === 1
      ? group[0]
      : ({
          ...group[0],
          id: `agg-${key}`,
          // A location id only identifies the row when the Location column is shown.
          locationId: dimensionKeys.includes("location")
            ? group[0].locationId
            : undefined,
          ...sumMetrics(group),
        } as T)
  );
};

export const buildLocationSummaryHierarchyRows = <T extends HierarchyBaseRow>(
  sourceRows: T[],
  mode: HierarchyMode,
  compare?: RowComparator<T>
): T[] => {
  if (mode === "flat") return sourceRows;

  const sortRows = (items: T[]): T[] => (compare ? [...items].sort(compare) : items);

  // The API may return several rows per location (e.g. one per year/period).
  // Time columns are not visible in hierarchy modes, so collapse to one row per location.
  const locationRows: T[] = Array.from(
    groupBy(
      sourceRows,
      (row) =>
        `${row.dimensions.region}|${row.dimensions.market}|${row.dimensions.location}`
    ).entries()
  ).map(([key, rows]) => ({ ...rows[0], id: key, ...sumMetrics(rows) }));

  const makeRollup = (
    rows: T[],
    id: string,
    groupType: HierarchyGroupType,
    dimensions: Record<string, string>,
    meta: HierarchyRowMeta
  ): T =>
    ({
      ...rows[0],
      id,
      locationId: undefined,
      dimensions: { ...rows[0].dimensions, ...dimensions },
      ...sumMetrics(rows),
      groupType,
      ...meta,
    } as T);

  const result: T[] = [];

  const pushMarket = (
    region: string,
    market: string,
    marketRows: T[],
    regionExpandKey: string | undefined
  ) => {
    const marketExpandKey = `market-${region}-${market}`;
    result.push(
      makeRollup(
        marketRows,
        marketExpandKey,
        "market",
        { region, market, location: "" },
        { parentRegionExpandKey: regionExpandKey, parentMarketExpandKey: undefined }
      )
    );
    sortRows(marketRows).forEach((row) =>
      result.push({
        ...row,
        groupType: "location",
        parentMarketExpandKey: marketExpandKey,
        parentRegionExpandKey: regionExpandKey,
      })
    );
  };

  // Sibling groups are ordered by their rolled-up values, so the active sort
  // applies to region / market rows too (not just to the raw API rows).
  const orderGroups = (
    groups: Map<string, T[]>,
    rollupOf: (rows: T[]) => T
  ): T[][] => {
    const entries = Array.from(groups.values());
    return compare
      ? entries
          .map((rows) => ({ rows, rollup: rollupOf(rows) }))
          .sort((a, b) => compare(a.rollup, b.rollup))
          .map((entry) => entry.rows)
      : entries;
  };

  if (mode === "region") {
    orderGroups(
      groupBy(locationRows, (row) => row.dimensions.region),
      (rows) =>
        makeRollup(rows, "", "region", { market: "", location: "" }, {})
    ).forEach((regionRows) => {
      const region = regionRows[0].dimensions.region;
      const regionExpandKey = `region-${region}`;
      result.push(
        makeRollup(
          regionRows,
          regionExpandKey,
          "region",
          { region, market: "", location: "" },
          { parentRegionExpandKey: undefined, parentMarketExpandKey: undefined }
        )
      );
      orderGroups(
        groupBy(regionRows, (row) => row.dimensions.market),
        (rows) => makeRollup(rows, "", "market", { location: "" }, {})
      ).forEach((marketRows) =>
        pushMarket(region, marketRows[0].dimensions.market, marketRows, regionExpandKey)
      );
    });
    return result;
  }

  // mode === "market"
  orderGroups(
    groupBy(
      locationRows,
      (row) => `${row.dimensions.region}|${row.dimensions.market}`
    ),
    (rows) => makeRollup(rows, "", "market", { location: "" }, {})
  ).forEach((marketRows) =>
    pushMarket(
      marketRows[0].dimensions.region,
      marketRows[0].dimensions.market,
      marketRows,
      undefined
    )
  );
  return result;
};

/** Same visibility rules as ASN's filterForVisibility (no search). */
export const filterLocationSummaryHierarchyRows = <T extends HierarchyRowMeta>(
  rows: T[],
  mode: HierarchyMode,
  expandedGroups: Set<string>
): T[] => {
  if (mode === "flat") return rows;
  return rows.filter((row) => {
    if (row.groupType === "region") return mode === "region";
    if (row.groupType === "market") {
      return (
        mode === "market" || expandedGroups.has(row.parentRegionExpandKey ?? "")
      );
    }
    if (row.groupType === "location") {
      const marketExpanded = expandedGroups.has(row.parentMarketExpandKey ?? "");
      return mode === "market"
        ? marketExpanded
        : marketExpanded &&
            expandedGroups.has(row.parentRegionExpandKey ?? "");
    }
    return false;
  });
};

/**
 * Which row dimension a visible column reads for each hierarchy level.
 * "region" mode has one column, so every level shows its own value.
 * "market" mode has two: the region column always shows the row's region and
 * the market column nests the location name on location rows
 * (same mapping as ASN's HIERARCHY_VALUE_SOURCE).
 */
export const hierarchySourceDimension = (
  row: HierarchyRowMeta,
  dimensionKey: string,
  mode: HierarchyMode
): string => {
  if (mode === "flat" || !row.groupType) return dimensionKey;
  if (mode === "region") {
    return dimensionKey === "region" ? row.groupType : "";
  }
  if (dimensionKey === "region") return "region";
  if (dimensionKey === "market") {
    return row.groupType === "location" ? "location" : "market";
  }
  return "";
};
